import { CommonModule } from '@angular/common';
import {
	Component,
	EventEmitter,
	forwardRef,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import {
	AbstractControl,
	ControlValueAccessor,
	FormControl,
	FormGroup,
	NG_VALUE_ACCESSOR,
	NgControl,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';

export type ToggleSize = 'small' | 'medium' | 'large';
export type ToggleStateType = 'default' | 'tenant' | 'indigo';

@Component({
	selector: 'app-toggle-reusable',
	standalone: true,
	imports: [CommonModule],
	templateUrl: './toggle-reusable.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ToggleReusable),
			multi: true,
		},
	],
})
export class ToggleReusable implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
	@Input() label = '';
	@Input() disabled = false;
	@Input() size: ToggleSize = 'medium';
	@Input() type: ToggleStateType = 'default';
	@Input() required = false;
	@Input() errorMessage = '';

	// Optional explicit bindings for contexts where component is not used via form directives.
	@Input() control: FormControl<boolean | null> | null = null;
	@Input() formGroup: FormGroup | null = null;
	@Input() formControlName = '';
	@Input() value = false;

	@Output() valueChange = new EventEmitter<boolean>();

	private onChange: (value: boolean) => void = () => {};
	private onTouched: () => void = () => {};
	private baseValidator: ValidatorFn | null = null;
	private disabledByInput = false;
	private controlValueSub?: Subscription;
	private subscribedControl: AbstractControl | null = null;
	public ngControl = inject(NgControl, { self: true, optional: true });

	constructor() {
		if (this.ngControl) {
			this.ngControl.valueAccessor = this;
		}
	}

	ngOnInit(): void {
		this.captureBaseValidator();
		this.applyRequiredValidation();
		this.syncDisabledState();
		this.syncValueFromControl();
		this.bindControlValueChanges();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['control'] || changes['formGroup'] || changes['formControlName']) {
			this.captureBaseValidator();
			this.applyRequiredValidation();
			this.syncDisabledState();
			this.syncValueFromControl();
			this.bindControlValueChanges();
		}

		if (changes['required']) {
			this.applyRequiredValidation();
		}

		if (changes['disabled']) {
			this.syncDisabledState();
		}
	}

	ngOnDestroy(): void {
		this.controlValueSub?.unsubscribe();
	}

	get resolvedControl(): AbstractControl | null {
		if (this.ngControl?.control) {
			return this.ngControl.control;
		}

		if (this.control) {
			return this.control;
		}

		if (this.formGroup && this.formControlName) {
			return this.formGroup.get(this.formControlName);
		}

		return null;
	}

	get isDisabled(): boolean {
		return this.disabled || !!this.resolvedControl?.disabled;
	}

	get isInvalid(): boolean {
		const control = this.resolvedControl;
		return !!(control?.invalid && (control.touched || control.dirty));
	}

	get isValid(): boolean {
		const control = this.resolvedControl;
		return !!(control?.valid && (control.touched || control.dirty));
	}

	get currentValue(): boolean {
		if (this.resolvedControl) {
			return !!this.resolvedControl.value;
		}

		return !!this.value;
	}

	get sizeClasses(): { track: string; thumb: string; translate: string } {
		if (this.size === 'small') {
			return {
				track: 'h-5 w-9',
				thumb: 'h-4 w-4',
				translate: 'translate-x-4',
			};
		}

		if (this.size === 'large') {
			return {
				track: 'h-8 w-14',
				thumb: 'h-6 w-6',
				translate: 'translate-x-6',
			};
		}

		return {
			track: 'h-6 w-11',
			thumb: 'h-5 w-5',
			translate: 'translate-x-5',
		};
	}

	get firstErrorMessage(): string {
		if (this.errorMessage.trim().length > 0) {
			return this.errorMessage;
		}

		if (this.resolvedControl?.errors?.['required']) {
			return `${this.label || 'This field'} is required.`;
		}

		return 'Invalid value.';
	}

	get trackColorClass(): string {
		if (this.type === 'tenant') {
			return this.currentValue ? 'border-blue600 bg-blue600' : 'border-slate200 bg-slate200';
		}

		if (this.type === 'indigo') {
			return this.currentValue ? 'border-indigo600 bg-indigo600' : 'border-slate400 bg-slate400';
		}

		return this.currentValue ? 'border-green600 bg-green600' : 'border-red600 bg-red600';
	}

	get validRingClass(): string {
		if (!this.isValid) {
			return '';
		}

		if (this.type === 'tenant') {
			return 'ring-2 ring-blue50';
		}

		if (this.type === 'indigo') {
			return 'ring-2 ring-indigo50';
		}

		return 'ring-2 ring-green50';
	}

	get labelClassMap(): Record<string, boolean> {
		if (this.type === 'tenant') {
			return {
				'text-red600': this.isInvalid,
				'text-neutral500': !this.isInvalid && !this.currentValue,
				'text-blue600': !this.isInvalid && this.currentValue,
			};
		}

		if (this.type === 'indigo') {
			return {
				'text-red600': this.isInvalid,
				'text-neutral500': !this.isInvalid && !this.currentValue,
				'text-indigo600': !this.isInvalid && this.currentValue,
			};
		}

		return {
			'text-red600': this.isInvalid || !this.currentValue,
			'text-green600': !this.isInvalid && this.currentValue,
		};
	}

	writeValue(value: boolean | null): void {
		this.value = !!value;
	}

	registerOnChange(fn: (value: boolean) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	toggle(): void {
		if (this.isDisabled) {
			return;
		}

		this.value = !this.value;

		if (!this.ngControl?.control && this.resolvedControl) {
			this.resolvedControl.setValue(this.value);
		}

		this.onChange(this.value);
		this.valueChange.emit(this.value);
		this.markAsTouched();
	}

	onKeydown(event: KeyboardEvent): void {
		if (event.key !== ' ' && event.key !== 'Enter') {
			return;
		}

		event.preventDefault();
		this.toggle();
	}

	markAsTouched(): void {
		this.onTouched();
		this.resolvedControl?.markAsTouched();
		this.resolvedControl?.markAsDirty();
		this.resolvedControl?.updateValueAndValidity();
	}

	private captureBaseValidator(): void {
		this.baseValidator = this.resolvedControl?.validator ?? null;
	}

	private applyRequiredValidation(): void {
		if (!this.resolvedControl) {
			return;
		}

		const validators: ValidatorFn[] = [];
		if (this.baseValidator) {
			validators.push(this.baseValidator);
		}

		if (this.required) {
			validators.push(Validators.requiredTrue);
		}

		this.resolvedControl.setValidators(validators);
		this.resolvedControl.updateValueAndValidity({ emitEvent: false });
	}

	private syncDisabledState(): void {
		if (!this.resolvedControl) {
			return;
		}

		if (this.disabled && this.resolvedControl.enabled) {
			this.resolvedControl.disable({ emitEvent: false });
			this.disabledByInput = true;
		}

		if (!this.disabled && this.disabledByInput && this.resolvedControl.disabled) {
			this.resolvedControl.enable({ emitEvent: false });
			this.disabledByInput = false;
		}
	}

	private syncValueFromControl(): void {
		if (this.resolvedControl) {
			this.value = !!this.resolvedControl.value;
		}
	}

	private bindControlValueChanges(): void {
		if (this.subscribedControl === this.resolvedControl) {
			return;
		}

		this.controlValueSub?.unsubscribe();
		this.subscribedControl = this.resolvedControl;

		if (!this.subscribedControl?.valueChanges) {
			return;
		}

		this.controlValueSub = this.subscribedControl.valueChanges.subscribe((value) => {
			this.value = !!value;
		});
	}
}
